import React from 'react';

// Formats a raw numeric string with thousand separators for display,
// preserving an optional leading minus sign.
function format(raw) {
  if (raw === '' || raw === null || raw === undefined) return '';
  let s = String(raw);
  const neg = s.startsWith('-');
  if (neg) s = s.slice(1);
  const [int, dec] = s.split('.');
  const intFmt = (int || '').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const body = dec !== undefined ? `${intFmt}.${dec}` : intFmt;
  return (neg ? '-' : '') + body;
}

const CONTROL_KEYS = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];

/**
 * Masked numeric text input:
 *  - renders a text field (not type=number)
 *  - shows a thousand separator while typing (e.g. 1,000,000)
 *  - rejects alphabet keystrokes on keydown
 *  - calls onChange with the UNMASKED numeric string (e.g. "1000000.5")
 *
 * value: raw numeric value/string ('' when empty). allowDecimal toggles the dot.
 */
export default function NumberInput({
  label,
  value,
  onChange,
  allowDecimal = true,
  allowNegative = false,
  required,
  error,
  disabled,
  placeholder,
  className = '',
}) {
  const display = format(value);

  const onKeyDown = (e) => {
    if (CONTROL_KEYS.includes(e.key) || e.ctrlKey || e.metaKey) return;
    if (e.key === '-') {
      // Only a single leading minus, and only when allowNegative is set.
      const atStart = e.target.selectionStart === 0;
      if (!allowNegative || !atStart || display.includes('-')) e.preventDefault();
      return;
    }
    if (e.key === '.') {
      if (!allowDecimal || display.includes('.')) e.preventDefault();
      return;
    }
    if (!/[0-9]/.test(e.key)) e.preventDefault(); // reject letters/symbols
  };

  const onInnerChange = (e) => {
    let raw = e.target.value.replace(/,/g, '');
    // Capture a single leading minus, then strip all minus signs from the body.
    const neg = allowNegative && raw.trim().startsWith('-');
    raw = raw.replace(/-/g, '');
    raw = allowDecimal ? raw.replace(/[^0-9.]/g, '') : raw.replace(/[^0-9]/g, '');
    const parts = raw.split('.');
    if (parts.length > 2) raw = `${parts[0]}.${parts.slice(1).join('')}`; // collapse extra dots
    onChange((neg ? '-' : '') + raw);
  };

  return (
    <label className="block">
      {label && (
        <span className="mb-1 block text-sm font-medium text-gray-700">
          {label}
          {required && <span className="text-red-500"> *</span>}
        </span>
      )}
      <input
        type="text"
        inputMode="decimal"
        value={display}
        disabled={disabled}
        placeholder={placeholder}
        onKeyDown={onKeyDown}
        onChange={onInnerChange}
        className={`w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand disabled:bg-gray-100 disabled:text-gray-500 ${
          error ? 'border-red-400' : 'border-gray-300'
        } ${className}`}
      />
      {error && <span className="mt-1 block text-xs text-red-600">{error}</span>}
    </label>
  );
}
